import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api, { fmtError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { EnvelopeSimple, Lock } from "@phosphor-icons/react";
import Logo from "../components/Logo";
import PasswordInput, { PASSWORD_MIN_LENGTH, validatePassword } from "../components/PasswordInput";
import { trackEvent } from "../components/GAScript";

function destinationFor(user) {
  if (user.must_change_password) return "/change-password";
  if (user.role === "admin" || user.role === "pm" || user.role === "researcher") return "/admin";
  if (user.is_cs_business) return "/business";
  if (user.has_global_intelligence_access || user.has_private_vault_access) return "/enterprise";
  // Anything else — patron, panelist, or a legacy growth_lead role that
  // hasn't been migrated yet — belongs in the panelist app.
  return "/panelist";
}

// Tiny helper hook: pulls site-content keys (cached via shared useSiteContent)
// and exposes T(key, fallback). Cached hook eliminates flash-of-default text on refresh.
import sharedUseSiteContent from "../lib/useSiteContent";
function useSiteContent() {
  const c = sharedUseSiteContent();
  return (key, fallback) => (c[key] && String(c[key]).trim() ? c[key] : fallback);
}

export function LoginPage() {
  const { user, setUser, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [searchParams] = useSearchParams();
  // PHASE 8.35 — cohort-aware wave invitation emails ship a `?email=` and
  // `?next=` query param so the patron lands in the right place after login
  // (typically /panelist/survey/<wave_id> for complete profiles).
  const emailFromLink = (searchParams.get("email") || "").toLowerCase().trim();
  const nextFromLink = searchParams.get("next") || "";
  const [email, setEmail] = useState(emailFromLink);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const T = useSiteContent();

  // PHASE 8.43 — World-class "already authenticated" handling. If the user
  // hits /login while a valid session is active, we don't re-render the
  // form (re-logging in would invalidate their JWT and burn a session).
  // Instead, redirect to their role-appropriate dashboard (or to ?next=
  // if a cohort-email deep-link was provided). Uses `replace: true` so the
  // bogus /login entry never appears in browser back-history. The check
  // waits for `authLoading` to settle so we don't redirect during the
  // first-paint hydration window.
  useEffect(() => {
    if (authLoading || !user) return;
    const dest = nextFromLink || destinationFor(user) || "/panelist";
    nav(dest, { replace: true });
  }, [user, authLoading, nextFromLink, nav]);

  // Hide the form entirely while we're either booting auth or about to
  // redirect — avoids the brief "login fields visible to a logged-in user"
  // flash that prompted this fix in the first place.
  if (authLoading || user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" data-testid="login-redirect-spinner">
        <div className="text-xs uppercase tracking-widest text-neutral-400">Redirecting…</div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) localStorage.setItem("pollpak_token", data.token);
      setUser(data.user);
      toast.success(`Welcome, ${data.user.name}`);
      // Priority order: explicit ?next= query (from cohort email) overrides
      // both role-based destination and from-state fallback.
      const dest = nextFromLink || destinationFor(data.user) || loc.state?.from || "/panelist";
      nav(dest);
    } catch (err) {
      if (err.response?.status === 409) {
        const nx = nextFromLink ? `&next=${encodeURIComponent(nextFromLink)}` : "";
        nav(`/set-password?email=${encodeURIComponent(email)}${nx}`);
        return;
      }
      toast.error(fmtError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2">
      <div className="hidden md:flex bg-black text-white p-12 items-end relative overflow-hidden">
        <div className="absolute top-12 left-12">
          <Logo to="/" inverted size="md" data-testid="login-logo" />
          <div className="overline text-mango mt-1" style={{ fontSize: '0.6rem' }}>{T("nav_subtitle", "MARKET INTELLIGENCE PAKISTAN")}</div>
        </div>
        <div>
          <div className="overline text-mango mb-4" data-testid="login-sidebar-overline">{T("login_sidebar_overline", "SIGN IN")}</div>
          <h2 className="text-5xl font-black tracking-tighter leading-none" data-testid="login-sidebar-title" dangerouslySetInnerHTML={{ __html: T("login_sidebar_title", "Welcome<br/>back to <span class=\"text-mango\">oodel.</span>") }} />
        </div>
      </div>

      <div className="flex flex-col justify-center p-8 md:p-16 max-w-xl w-full mx-auto">
        <div className="overline mb-3 flex items-center gap-2"><EnvelopeSimple size={14} /> SIGN IN</div>
        <h1 className="text-4xl font-black tracking-tighter mb-2">Sign in to your account</h1>
        <p className="text-neutral-600 mb-6 text-sm">Enter your email and password.</p>

        <form onSubmit={submit} className="space-y-5" data-testid="login-form">
          <div>
            <Label className="overline">Email</Label>
            <Input data-testid="login-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none border-0 border-b-2 px-0 focus-visible:ring-0 text-lg" autoFocus />
          </div>
          <div>
            <Label className="overline">Password</Label>
            <PasswordInput testId="login-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" autoComplete="current-password" variant="underline" className="text-lg" />
          </div>
          <Button data-testid="login-submit" disabled={loading} type="submit" className="w-full rounded-none bg-black hover:bg-neutral-800 py-6 text-base">
            {loading ? "Signing in…" : "Sign in →"}
          </Button>
        </form>

        <div className="mt-6 flex items-center justify-between text-sm">
          <Link to="/forgot-password" className="text-neutral-600 hover:text-black underline" data-testid="forgot-password-link">Forgot your password?</Link>
          <Link to="/register" className="text-black font-semibold underline" data-testid="go-register">Join the panel</Link>
        </div>
      </div>
    </div>
  );
}

export function RegisterPage() {
  const nav = useNavigate();
  const loc = useLocation();
  // Referral attribution: prefer ?ref= in URL, fall back to the localStorage
  // key set by the homepage (FoundingPatronCTA / PublicHome). This makes the
  // share-link flow work even when admin closes the waitlist — the recipient
  // lands on /?ref=CODE, clicks "Join Panel" → /register, and we still pick
  // up the referral from localStorage.
  const refFromUrl = (() => {
    const q = new URLSearchParams(loc.search).get("ref");
    if (q) {
      try { localStorage.setItem("oodel_ref_code", q); } catch { /* no-op */ }
      return q;
    }
    try { return localStorage.getItem("oodel_ref_code") || ""; } catch { return ""; }
  })();
  // Campus-ambassador slug stamped by /cap/:slug landing page. Picked up
  // silently here so the resulting account is attributed to the CA's
  // cash-payout track instead of the peer referral track.
  const capSlugFromStorage = (() => {
    try { return localStorage.getItem("oodel_cap_slug") || ""; } catch { return ""; }
  })();
  const [form, setForm] = useState({
    name: "", email: "", password: "", password2: "",
    phone_number: "",
    referral_code: refFromUrl,
  });
  const [loading, setLoading] = useState(false);
  // Two-step signup UI: "form" → user fills name/email/password →
  // "otp" → user enters 6-digit code from email → JWT issued, redirect to /panelist/profile.
  const [step, setStep] = useState("form");
  const [otp, setOtp] = useState("");
  const [resending, setResending] = useState(false);
  const { setUser } = useAuth();
  const T = useSiteContent();

  const submit = async (e) => {
    e.preventDefault();
    const pwErr = validatePassword(form.password);
    if (pwErr) { toast.error(pwErr); return; }
    if (form.password !== form.password2) { toast.error("Passwords don't match"); return; }
    // If this email already exists as a passwordless legacy-waitlist stub,
    // skip the signup flow entirely and route to /set-password — they already
    // have an account, they just need to claim it.
    try {
      const probe = await api.get(`/auth/account-state?email=${encodeURIComponent(form.email)}`);
      if (probe.data?.exists && probe.data?.needs_password_set && probe.data?.is_legacy_waitlist) {
        nav(`/set-password?email=${encodeURIComponent(form.email)}`, { replace: true });
        return;
      }
    } catch (_) { /* probe is best-effort; carry on with normal signup */ }
    setLoading(true);
    try {
      const body = {
        name: form.name, email: form.email, password: form.password,
        phone_number: null,  // captured later in /panelist/profile
        is_pakistan_resident: true,        // default for v1; can be edited later in profile if admin enables overseas
        country_of_residence: null,
        referral_code: form.referral_code || null,
        cap_slug: capSlugFromStorage || null,
      };
      const { data } = await api.post("/auth/register", body);
      // Backend always returns requires_verification=true now — no JWT until OTP confirmed.
      if (data.requires_verification) {
        toast.success("Check your email for a 6-digit verification code.");
        setStep("otp");
      } else if (data.token) {
        // Legacy path safety net (should not happen).
        localStorage.setItem("pollpak_token", data.token);
        setUser(data.user);
        nav("/panelist/profile");
      }
    } catch (err) {
      toast.error(fmtError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-signup-otp", {
        email: form.email, code: otp.trim(),
      });
      localStorage.setItem("pollpak_token", data.token);
      setUser(data.user);
      trackEvent("signup_complete", { method: "email_otp", has_referral: !!form.referral_code });
      toast.success("Email verified! Complete your profile to earn your welcome bonus.");
      nav("/panelist/profile");
    } catch (err) {
      toast.error(fmtError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-signup-otp", { email: form.email });
      toast.success("New code sent — check your inbox.");
    } catch (err) {
      toast.error(fmtError(err.response?.data?.detail) || err.message);
    } finally { setResending(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2">
      <div className="hidden md:flex bg-[#0A0A0A] text-white p-12 items-end relative">
        <div className="absolute top-12 left-12">
          <Logo to="/" inverted size="md" data-testid="register-logo" />
        </div>
        <div>
          <div className="overline text-mango mb-4" data-testid="register-sidebar-overline">{T("register_sidebar_overline", "JOIN THE PANEL")}</div>
          <h2 className="text-5xl font-black tracking-tighter leading-none" data-testid="register-sidebar-title" dangerouslySetInnerHTML={{ __html: T("register_sidebar_title", "Earn points.<br/><span class=\"text-mango\">Redeem rewards.</span>") }} />
          <p className="mt-6 text-neutral-300 max-w-md" data-testid="register-sidebar-body">{T("register_sidebar_body", "Each completed survey earns 50 points. Cash out for Foodpanda, Daraz, EasyPaisa, or donate to charity.")}</p>
        </div>
      </div>
      <div className="flex flex-col justify-center p-8 md:p-16 max-w-xl w-full mx-auto">
        <div className="overline mb-3">{step === "otp" ? "VERIFY YOUR EMAIL" : "SIGN UP"}</div>
        <h1 className="text-4xl font-black tracking-tighter mb-8">
          {step === "otp" ? "Enter the 6-digit code" : "Create your account"}
        </h1>

        {step === "form" ? (
          <form onSubmit={submit} className="space-y-5" data-testid="register-form">
            <div><Label className="overline">Full name</Label><Input data-testid="register-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none border-0 border-b-2 px-0 focus-visible:ring-0" /></div>
            <div><Label className="overline">Email</Label><Input data-testid="register-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none border-0 border-b-2 px-0 focus-visible:ring-0" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="overline">Password</Label><PasswordInput testId="register-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={`Min ${PASSWORD_MIN_LENGTH} characters`} autoComplete="new-password" variant="underline" /></div>
              <div><Label className="overline">Confirm password</Label><PasswordInput testId="register-password2" value={form.password2} onChange={(e) => setForm({ ...form, password2: e.target.value })} placeholder="Re-enter password" autoComplete="new-password" variant="underline" /></div>
            </div>
            {T("signup_phone_disclaimer_enabled", "true").toLowerCase() !== "false" && (
              <p className="text-xs text-neutral-500 -mt-2" data-testid="signup-phone-disclaimer">
                {T("signup_phone_disclaimer_text", "We'll ask for your phone (verified via OTP) when you complete your profile after sign-up — keeps this first step lighter.")}
              </p>
            )}
            {form.referral_code && (
              <div className="bg-mango/10 border-l-4 border-mango p-3 text-sm" data-testid="register-ref-badge">
                Referred by code: <span className="font-mono font-bold">{form.referral_code}</span>
              </div>
            )}
            <Button data-testid="register-submit" disabled={loading} type="submit" className="w-full rounded-none bg-mango hover:bg-oodel-dark py-6 text-base text-white border-2 border-black hard-shadow">
              {loading ? "Sending code…" : "Create my account →"}
            </Button>
          </form>
        ) : (
          <form onSubmit={verify} className="space-y-5" data-testid="register-otp-form">
            <p className="text-sm text-neutral-600">
              We sent a 6-digit code to <strong data-testid="register-otp-email">{form.email}</strong>.
              Enter it below to activate your account.
            </p>
            <Input
              data-testid="register-otp-code"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              maxLength={6}
              required
              className="text-3xl font-mono text-center tracking-[0.5em] rounded-none border-2 py-6"
              placeholder="000000"
              autoFocus
            />
            <Button data-testid="register-otp-submit" disabled={loading || otp.length !== 6} type="submit" className="w-full rounded-none bg-mango hover:bg-oodel-dark py-6 text-base text-white border-2 border-black hard-shadow">
              {loading ? "Verifying…" : "Verify & continue →"}
            </Button>
            <div className="text-sm text-center text-neutral-600">
              Didn't get the code?{" "}
              <button type="button" onClick={resend} disabled={resending} className="text-black font-semibold underline disabled:opacity-50" data-testid="register-otp-resend">
                {resending ? "Sending…" : "Resend code"}
              </button>
              {" · "}
              <button type="button" onClick={() => setStep("form")} className="text-black font-semibold underline" data-testid="register-otp-back">
                Change email
              </button>
            </div>
          </form>
        )}

        <div className="mt-6 text-sm text-neutral-600">Already a member? <Link to="/login" className="text-black font-semibold underline">Sign in</Link></div>
      </div>
    </div>
  );
}

// ---------------- CHANGE PASSWORD (forced on first login for admin-created accounts) ----------------
export function ChangePasswordPage() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw1.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw1 !== pw2) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      const { data } = await api.put("/auth/password", { new_password: pw1 });
      // ITER 81 — the backend just revoked every outstanding token for this
      // user (incl. the one we're using right now) and minted a fresh one.
      // Swap it in BEFORE calling refresh(), otherwise refresh() would use
      // the now-dead old token and surface a false "Session ended" error.
      if (data?.token) localStorage.setItem("pollpak_token", data.token);
      const fresh = await refresh();
      toast.success("Password updated.");
      nav(destinationFor(fresh));
    } catch (err) {
      toast.error(fmtError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  if (!user) return <div className="p-12">Please sign in first.</div>;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] p-6">
      <div className="bg-white border-2 border-black hard-shadow w-full max-w-md p-8" data-testid="change-password-card">
        <div className="overline mb-3 flex items-center gap-2 text-mango"><Lock size={14} /> SET A NEW PASSWORD</div>
        <h1 className="text-3xl font-black tracking-tighter mb-2">{user.must_change_password ? "First-login password change" : "Change password"}</h1>
        <p className="text-neutral-600 mb-6 text-sm">{user.must_change_password ? "Your account was created by an admin. Please set your own password to continue." : "Update your account password."}</p>
        <form onSubmit={submit} className="space-y-5">
          <div><Label className="overline">New password</Label><PasswordInput testId="cp-new" value={pw1} onChange={(e) => setPw1(e.target.value)} placeholder={`Min ${PASSWORD_MIN_LENGTH} characters`} autoComplete="new-password" variant="underline" autoFocus /></div>
          <div><Label className="overline">Confirm new password</Label><PasswordInput testId="cp-confirm" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Re-enter password" autoComplete="new-password" variant="underline" /></div>
          <Button data-testid="cp-submit" disabled={loading} type="submit" className="w-full rounded-none bg-black hover:bg-neutral-800 py-6 text-base text-white">{loading ? "Saving…" : "Save new password →"}</Button>
        </form>
      </div>
    </div>
  );
}

// ---------------- FORGOT PASSWORD (self-serve via email link) ----------------
// World-class flow:
//   1. User enters email
//   2. POST /auth/forgot-password — always returns 200 (anti-enumeration)
//   3. Show "check your email" success state regardless of whether the email exists.
//      This prevents an attacker from probing valid emails by watching error messages.
//   4. The user then clicks the link in the Resend email → /reset-password?token=…
export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      // Whether or not the email exists, show the same success state.
      setSent(true);
    } catch (err) {
      // Network errors only — server returns 200 even for unknown emails.
      toast.error(fmtError(err) || "Couldn't reach the server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] p-6">
        <div className="bg-white border-2 border-black hard-shadow w-full max-w-md p-8 text-center" data-testid="forgot-success">
          <div className="overline mb-3 text-mango">CHECK YOUR INBOX</div>
          <h1 className="text-3xl font-black tracking-tighter mb-3">Email on the way</h1>
          <p className="text-neutral-700 mb-2 text-sm">
            If an account exists for <strong className="text-black">{email}</strong>, we just sent a password-reset link to it.
          </p>
          <p className="text-neutral-500 mb-6 text-xs">The link is valid for <strong>60 minutes</strong> and can only be used once. Don't see it? Check your spam folder, or try again in 15 minutes.</p>
          <Link to="/login"><Button className="rounded-none bg-black hover:bg-neutral-800 w-full" data-testid="back-to-login">← Back to sign-in</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] p-6">
      <div className="bg-white border-2 border-black hard-shadow w-full max-w-md p-8" data-testid="forgot-password-card">
        <div className="overline mb-3 text-mango">PASSWORD RESET</div>
        <h1 className="text-3xl font-black tracking-tighter mb-3">Forgot your password?</h1>
        <p className="text-neutral-600 mb-6 text-sm">
          Enter the email you signed up with. We'll send you a secure link to set a new password.
        </p>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label className="overline">Email address</Label>
            <Input
              data-testid="forgot-email"
              type="email"
              required
              autoFocus
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-none border-0 border-b-2 px-0 focus-visible:ring-0"
              placeholder="you@example.com"
            />
          </div>
          <Button data-testid="forgot-submit" disabled={loading || !email.trim()} type="submit" className="w-full rounded-none bg-black hover:bg-neutral-800 py-6 text-base text-white">
            {loading ? "Sending…" : "Send reset link →"}
          </Button>
          <Link to="/login" className="block text-center text-xs text-neutral-500 hover:text-black underline" data-testid="forgot-cancel">Back to sign-in</Link>
        </form>
      </div>
    </div>
  );
}


// ---------------- RESET PASSWORD (the page the email link points at) ----------------
// Flow:
//   1. /reset-password?token=XYZ — parse token from URL
//   2. User enters new password twice (with strength feedback)
//   3. POST /auth/reset-password with token + new password
//   4. On success → toast + redirect to /login
//   5. On expired/used/invalid → friendly error + "request a new link" button
export function ResetPasswordPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const token = params.get("token") || "";

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Mid-strength feedback. Not gating — just informative.
  const strength = (() => {
    if (pw1.length === 0) return { label: "", color: "" };
    if (pw1.length < 6) return { label: "Too short", color: "text-red-600" };
    if (pw1.length < 8) return { label: "Weak", color: "text-amber-600" };
    if (!/[0-9]/.test(pw1) && !/[A-Z]/.test(pw1)) return { label: "OK", color: "text-amber-600" };
    if (pw1.length >= 12 && /[0-9]/.test(pw1) && /[A-Za-z]/.test(pw1)) return { label: "Strong", color: "text-green-700" };
    return { label: "Good", color: "text-green-600" };
  })();
  const mismatch = pw2.length > 0 && pw1 !== pw2;
  const canSubmit = token && pw1.length >= 6 && pw1 === pw2 && !loading;

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] p-6">
        <div className="bg-white border-2 border-black hard-shadow w-full max-w-md p-8 text-center" data-testid="reset-no-token">
          <div className="overline mb-3 text-red-600">MISSING LINK</div>
          <h1 className="text-2xl font-black tracking-tighter mb-3">This reset link is incomplete</h1>
          <p className="text-neutral-600 mb-6 text-sm">Please use the link in your most recent password-reset email, or request a new one.</p>
          <Link to="/forgot-password"><Button className="rounded-none bg-black hover:bg-neutral-800 w-full">Request a new link</Button></Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pw1 });
      toast.success("Password updated. Please sign in.");
      // Pre-fill the email on the login page so it's one tap away.
      nav("/login", { replace: true, state: { email: data?.email } });
    } catch (err) {
      setError(fmtError(err) || "Couldn't reset your password. Please request a new link.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] p-6">
      <div className="bg-white border-2 border-black hard-shadow w-full max-w-md p-8" data-testid="reset-password-card">
        <div className="overline mb-3 text-mango">NEW PASSWORD</div>
        <h1 className="text-3xl font-black tracking-tighter mb-3">Choose a new password</h1>
        <p className="text-neutral-600 mb-6 text-sm">Pick something you'll remember — minimum 6 characters. We'll sign you in after.</p>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label className="overline">New password</Label>
            <Input
              data-testid="reset-pw1"
              type="password"
              required
              autoFocus
              autoComplete="new-password"
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              className="rounded-none border-0 border-b-2 px-0 focus-visible:ring-0"
            />
            {strength.label && (
              <div className={`text-xs mt-1 font-bold ${strength.color}`} data-testid="reset-strength">Strength: {strength.label}</div>
            )}
          </div>
          <div>
            <Label className="overline">Confirm new password</Label>
            <Input
              data-testid="reset-pw2"
              type="password"
              required
              autoComplete="new-password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className="rounded-none border-0 border-b-2 px-0 focus-visible:ring-0"
            />
            {mismatch && <div className="text-xs mt-1 font-bold text-red-600" data-testid="reset-mismatch">Passwords don't match</div>}
          </div>
          {error && <div className="border-2 border-red-600 bg-red-50 text-red-800 p-3 text-sm" data-testid="reset-error">{error}</div>}
          <Button data-testid="reset-submit" disabled={!canSubmit} type="submit" className="w-full rounded-none bg-black hover:bg-neutral-800 py-6 text-base text-white">
            {loading ? "Saving…" : "Set new password →"}
          </Button>
          <Link to="/login" className="block text-center text-xs text-neutral-500 hover:text-black underline">Cancel and go back to sign-in</Link>
        </form>
      </div>
    </div>
  );
}
